import { useOutletContext } from 'react-router'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { useTabParam } from '@structural-climbing/ui'
import {
  type ProgressDiscipline,
  type ProgressPeriod,
  useMyProgress,
} from '../features/progress/useMyProgress'
import { ProgressHero } from '../features/progress/ProgressHero'
import { ActivityChart } from '../features/progress/ActivityChart'
import { GradeComparisonChart } from '../features/progress/GradeComparisonChart'
import { HardestTopCard } from '../features/progress/HardestTopCard'
import { DeltaChip } from '../features/progress/DeltaChip'
import { formatDuration } from '../features/progress/progressMath'
import { Page } from '../design/Page'
import { Section } from '../design/Section'
import { SurfaceCard } from '../design/SurfaceCard'
import { StatTile } from '../design/StatTile'
import { ChipTabs } from '../design/ChipTabs'
import { EmptyState } from '../design/EmptyState'
import { SkeletonCard } from '../design/Skeleton'
import { fadeRise } from '../design/motion'
import type { GymContext } from '../layouts/GymLayout'
import { useGymDisciplines } from '../features/gym/useGymDisciplines'

const DISCIPLINES = ['all', 'boulder', 'lead'] as const satisfies readonly ProgressDiscipline[]
const PERIODS = ['week', 'month', 'quarter'] as const satisfies readonly ProgressPeriod[]

/** A tile for a figure that isn't a plain count, such as a duration. */
function TextTile({
  label,
  value,
  current,
  previous,
}: {
  label: string
  value: string
  current: number
  previous: number
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-semibold leading-none">{value}</span>
      </div>
      <span className="flex items-center gap-1.5 text-sm text-foreground/45">
        {label}
        <DeltaChip current={current} previous={previous} />
      </span>
    </div>
  )
}

export function ProgressPage() {
  const { gymId } = useOutletContext<GymContext>()
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // A gym with one discipline has nothing to filter by: no control, and the
  // page is that discipline. A ?discipline= naming the other one is ignored.
  const gymDisciplines = useGymDisciplines()
  const single = gymDisciplines.length === 1
  const [discipline, setDiscipline] = useTabParam(
    'discipline',
    single ? gymDisciplines : DISCIPLINES,
    single ? gymDisciplines[0] : 'all',
  )
  const [period, setPeriod] = useTabParam('period', PERIODS, 'month')
  const { data, loading, error } = useMyProgress(
    gymId,
    apiUrl,
    getAccessTokenSilently,
    discipline,
    period,
  )

  const periodLabel = t(`progress.period_${period}`)
  const hasAnything = data && (data.allTime.tops > 0 || data.allTime.visits > 0)

  const avgSession = (s: { minutes: number; visits: number }) =>
    s.visits ? Math.round(s.minutes / s.visits) : 0
  const flashRate = (s: { flashes: number; tops: number }) =>
    s.tops ? Math.round((s.flashes / s.tops) * 100) : 0

  return (
    <Page>
      <h1 className="sr-only">{t('progress.pageTitle')}</h1>

      {/* Two filters, one row of chips each, like Standings: which climbing,
          and over how long. Both live in the URL so a refresh keeps them. */}
      <motion.div variants={fadeRise} className="flex flex-col gap-2">
        {!single && (
          <ChipTabs
            aria-label={t('progress.disciplineFilterLabel')}
            value={discipline}
            onChange={setDiscipline}
            options={[
              { key: 'all', label: t('progress.disciplineAll') },
              { key: 'boulder', label: t('routes.disciplineBouldering') },
              { key: 'lead', label: t('routes.disciplineLead') },
            ]}
          />
        )}
        <ChipTabs
          aria-label={t('progress.periodFilterLabel')}
          value={period}
          onChange={setPeriod}
          options={PERIODS.map((p) => ({ key: p, label: t(`progress.period_${p}`) }))}
        />
      </motion.div>

      {loading && !data ? (
        <div className="flex flex-col gap-3">
          <SkeletonCard lines={3} />
          <SkeletonCard lines={4} />
        </div>
      ) : error || !data ? (
        <SurfaceCard className="p-5">
          <p className="text-sm text-danger">{t('common.errorDefault')}</p>
        </SurfaceCard>
      ) : !hasAnything ? (
        <EmptyState icon="🧗" title={t('progress.empty')} />
      ) : (
        <>
          <motion.div variants={fadeRise}>
            <ProgressHero data={data} periodLabel={periodLabel} />
          </motion.div>

          <Section title={t('progress.thisPeriodTitle', { period: periodLabel })}>
            <SurfaceCard className="p-5">
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
                <StatTile
                  label={t('progress.tops')}
                  value={data.current.tops}
                  prev={data.previous.tops}
                />
                <StatTile
                  label={t('progress.flashes')}
                  value={data.current.flashes}
                  prev={data.previous.flashes}
                  accent="⚡"
                />
                <TextTile
                  label={t('progress.flashRate')}
                  value={`${flashRate(data.current)}%`}
                  current={flashRate(data.current)}
                  previous={flashRate(data.previous)}
                />
                <StatTile
                  label={t('progress.visits')}
                  value={data.current.visits}
                  prev={data.previous.visits}
                />
                <TextTile
                  label={t('progress.timeInGym')}
                  value={formatDuration(data.current.minutes)}
                  current={data.current.minutes}
                  previous={data.previous.minutes}
                />
                <TextTile
                  label={t('progress.avgSession')}
                  value={formatDuration(avgSession(data.current))}
                  current={avgSession(data.current)}
                  previous={avgSession(data.previous)}
                />
              </div>
            </SurfaceCard>
          </Section>

          <Section>
            <ActivityChart data={data} locale={i18n.language} />
          </Section>

          <Section>
            <HardestTopCard data={data} periodLabel={periodLabel} />
          </Section>

          {data.byGrade.boulder && (
            <Section>
              <GradeComparisonChart discipline="boulder" rows={data.byGrade.boulder} />
            </Section>
          )}
          {data.byGrade.lead && (
            <Section>
              <GradeComparisonChart discipline="lead" rows={data.byGrade.lead} />
            </Section>
          )}

          <Section title={t('progress.allTimeTitle')}>
            <SurfaceCard className="p-5">
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                <StatTile label={t('progress.tops')} value={data.allTime.tops} />
                <StatTile
                  label={t('ascents.firstAscents')}
                  value={data.allTime.firstAscents}
                  accent="👑"
                />
                <StatTile label={t('progress.points')} value={data.allTime.points} />
                <div className="flex flex-col gap-1">
                  <span className="text-3xl font-semibold leading-none">
                    {formatDuration(data.allTime.minutes)}
                  </span>
                  <span className="text-sm text-foreground/45">{t('progress.timeInGym')}</span>
                </div>
              </div>
            </SurfaceCard>
          </Section>
        </>
      )}
    </Page>
  )
}
